import {
  IChatLesson,
  IChatLessonMetadata,
  IChatLessonStepMetadata,
} from "@model";
import {
  CHARACTER_LANGUAGES,
  type CharacterLanguageKey,
} from "../Character/characterLanguages";

export const CHAT_LESSON_LANGUAGES = CHARACTER_LANGUAGES;

export type ChatLessonLanguageKey = CharacterLanguageKey;

export function legacyLanguageKey(language?: string): string {
  if (!language?.trim()) return "en";
  return language.trim().toLowerCase().split("-")[0];
}

export function voiceCodeForLanguageKey(key: string): string {
  const hit = CHAT_LESSON_LANGUAGES.find((lang) => lang.key === key);
  return hit?.voiceCode ?? key;
}

export function normalizeChatLessonMetadata(
  metadata?: IChatLessonMetadata | null,
): IChatLessonMetadata {
  if (!metadata || typeof metadata !== "object") return {};
  return Object.fromEntries(
    Object.entries(metadata).filter(
      ([, value]) => value && typeof value === "object",
    ),
  );
}

export function cleanChatLessonMetadata(
  metadata?: IChatLessonMetadata | null,
): IChatLessonMetadata {
  const normalized = normalizeChatLessonMetadata(metadata);
  return Object.fromEntries(
    Object.entries(normalized)
      .filter(([, value]) => {
        const title = value?.title?.trim();
        const description = value?.description?.trim();
        return Boolean(title || description);
      })
      .map(([key, value]) => [
        key,
        {
          ...(value?.title?.trim() ? { title: value.title.trim() } : {}),
          ...(value?.description?.trim()
            ? { description: value.description.trim() }
            : {}),
        },
      ]),
  );
}

export function normalizeStepMetadata(
  metadata?: IChatLessonStepMetadata | null,
): IChatLessonStepMetadata {
  if (!metadata || typeof metadata !== "object") return {};
  return Object.fromEntries(
    Object.entries(metadata).filter(
      ([, value]) => value && typeof value === "object",
    ),
  );
}

export function cleanStepMetadata(
  metadata?: IChatLessonStepMetadata | null,
  lessonLangKeys?: string[],
): IChatLessonStepMetadata {
  const normalized = normalizeStepMetadata(metadata);
  const keys = lessonLangKeys?.length
    ? lessonLangKeys
    : Object.keys(normalized);
  return Object.fromEntries(
    keys
      .map((key) => {
        const title = normalized[key]?.title?.trim();
        const voiceName = normalized[key]?.voiceName?.trim();
        const starterText = normalized[key]?.starterText?.trim();
        if (!title && !voiceName && !starterText) return null;
        return [
          key,
          {
            ...(title ? { title } : {}),
            ...(voiceName ? { voiceName } : {}),
            ...(starterText ? { starterText } : {}),
          },
        ] as const;
      })
      .filter(
        (
          entry,
        ): entry is [
          string,
          { title?: string; voiceName?: string; starterText?: string },
        ] => Boolean(entry),
      ),
  );
}

export function getConfiguredLessonLanguageKeys(
  metadata?: IChatLessonMetadata | null,
): string[] {
  return Object.keys(cleanChatLessonMetadata(metadata));
}

/** Panel list label — English app title, then other metadata, then legacy top-level title. */
export function getLessonListTitle(
  lesson: Partial<IChatLesson>,
  preferLang = "en",
): string {
  const meta = normalizeChatLessonMetadata(lesson.metadata);
  const preferred = meta[preferLang]?.title?.trim();
  if (preferred) return preferred;

  for (const lang of CHAT_LESSON_LANGUAGES) {
    const title = meta[lang.key]?.title?.trim();
    if (title) return title;
  }

  const panelTitle =
    typeof lesson.title === "string" ? lesson.title.trim() : "";
  if (panelTitle) return panelTitle;

  return "Untitled";
}

export function resolveLessonListTitleFromMetadata(
  metadata?: IChatLessonMetadata | null,
): string | undefined {
  const cleaned = cleanChatLessonMetadata(metadata);
  const enTitle = cleaned.en?.title?.trim();
  if (enTitle) return enTitle;
  for (const lang of CHAT_LESSON_LANGUAGES) {
    const title = cleaned[lang.key]?.title?.trim();
    if (title) return title;
  }
  return undefined;
}

export function getLessonLanguageLabels(
  metadata?: IChatLessonMetadata | null,
): string[] {
  return getConfiguredLessonLanguageKeys(metadata).map((key) => {
    const hit = CHAT_LESSON_LANGUAGES.find((lang) => lang.key === key);
    return hit?.label ?? key;
  });
}
